import { Logo } from '../ui/Logo';

interface LegalPageProps {
  title: string;
  children: React.ReactNode;
  onBack: () => void;
}


/* ------------------------------------------------------------------ */
/*  Seller details — REQUIRED BEFORE THE PADDLE SUBMISSION              */
/*                                                                      */
/*  Traders selling to consumers must publish their identity and a      */
/*  geographic address. As a sole trader that means a personal name and */
/*  usually a home address, on a public page, indexed. A company name    */
/*  and a registered office (a gestoría's address is fine) avoids both,  */
/*  which is one of several reasons to incorporate before charging.      */
/*                                                                      */
/*  Until this is filled in, the identity paragraphs are omitted rather  */
/*  than rendered with placeholders in them — publishing "[YOUR FULL     */
/*  LEGAL NAME]" is worse than publishing nothing.                       */
/* ------------------------------------------------------------------ */
const SELLER = {
  /** Registered company name, or your legal name if trading personally. */
  name: '',
  /** Trading name shown to customers. */
  tradingAs: 'Mozayc',
  /** Registered office or business address. Not a home address if avoidable. */
  address: '',
  /** Country of registration — governs the terms. */
  country: '',
  /** Company number, or tax identifier if a sole trader. */
  taxId: '',
  email: 'hello@mozayc.io',
};

/** True once the details above are real. Gates the identity paragraphs. */
const SELLER_CONFIGURED = Boolean(SELLER.name && SELLER.address && SELLER.country);

const LAST_UPDATED = '10 September 2026';

export function LegalPage({ title, children, onBack }: LegalPageProps) {
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--surface-primary)', color: 'var(--content-primary)', '--accent-primary': '#2a9d8f' } as React.CSSProperties}>
      <nav
        className="sticky top-0 z-50"
        style={{
          backgroundColor: 'var(--surface-primary)',
          borderBottom: '1px solid var(--border-primary)',
          boxShadow: 'var(--shadow-sm)',
        }}
      >
        <div className="max-w-3xl mx-auto px-6 h-14 flex items-center justify-between">
          <button onClick={onBack} className="flex items-center gap-2">
            <Logo size="md" />
          </button>
          <button
            onClick={onBack}
            className="text-sm font-medium transition-colors"
            style={{ color: 'var(--content-secondary)' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--content-primary)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--content-secondary)'; }}
          >
            Back
          </button>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <h1
          className="text-3xl font-bold tracking-tight mb-2"
          style={{ color: 'var(--content-primary)' }}
        >
          {title}
        </h1>
        <p className="text-sm mb-10" style={{ color: 'var(--content-tertiary)' }}>
          Last updated: {LAST_UPDATED}
        </p>

        <div
          className="prose-legal space-y-6 text-sm leading-relaxed"
          style={{ color: 'var(--content-secondary)' }}
        >
          {children}
        </div>
      </main>

      <footer
        className="py-6 px-6"
        style={{ borderTop: '1px solid var(--border-primary)' }}
      >
        <div className="max-w-3xl mx-auto text-center">
          <span className="text-xs" style={{ color: 'var(--content-tertiary)' }}>
            &copy; 2026 Mozayc
          </span>
        </div>
      </footer>
    </div>
  );
}

const H2 = ({ children }: { children: React.ReactNode }) => (
  <h2 className="text-lg font-semibold mt-8 mb-3" style={{ color: 'var(--content-primary)' }}>
    {children}
  </h2>
);

const P = ({ children }: { children: React.ReactNode }) => (
  <p className="mb-3">{children}</p>
);

const UL = ({ children }: { children: React.ReactNode }) => (
  <ul className="list-disc pl-5 space-y-1 mb-3">{children}</ul>
);

export function PrivacyPolicy({ onBack }: { onBack: () => void }) {
  return (
    <LegalPage title="Privacy Policy" onBack={onBack}>
      <P>
        Mozayc ("we", "us", "our") operates the mozayc.io website and application.
        This page informs you of our policies regarding the collection, use, and
        disclosure of personal data when you use our service.
      </P>

      <H2>Information We Collect</H2>
      <P>We collect the following types of information:</P>
      <UL>
        <li><strong>Account information:</strong> Your email address, used for authentication and communication.</li>
        <li><strong>Calendar data:</strong> Events, tasks, tags, and other content you create within the app.</li>
        <li><strong>Shared spaces:</strong> When you collaborate with others, space membership and roles are stored.</li>
        <li><strong>Historic waitlist:</strong> Email addresses collected during the closed beta are retained from that period. Signup is now open and no new waitlist entries are created.</li>
        <li><strong>Usage data:</strong> Anonymous analytics to help us improve the product (via Vercel Analytics).</li>
        <li><strong>Error reports:</strong> Anonymized error data to help us fix bugs (via Sentry). No personally identifiable information is sent.</li>
        <li><strong>Local preferences:</strong> Theme, view settings, and other preferences stored in your browser's local storage.</li>
      </UL>

      <H2>How We Use Your Information</H2>
      <UL>
        <li>To provide and maintain the service.</li>
        <li>To authenticate your identity and secure your account.</li>
        <li>To send you important updates about the service (e.g., beta access invitations).</li>
        <li>To monitor and improve the reliability and performance of the app.</li>
      </UL>
      <P>We will never sell your personal data to third parties.</P>

      <H2>Data Storage and Security</H2>
      <P>
        Your data is stored securely using Supabase, which provides encryption at rest
        and in transit. We use row-level security policies to ensure you can only access
        your own data (or data explicitly shared with you through spaces).
      </P>

      <H2>Who else processes your data</H2>
      <P>
        We use the following sub-processors. Each handles data only to provide the
        Service, and none of them sell it.
      </P>
      <UL>
        <li><strong>Supabase</strong> — database, authentication and file storage. Holds
          everything you create.</li>
        <li><strong>Vercel</strong> — hosting, plus anonymous page analytics that set no
          cookies and do not identify you.</li>
        <li><strong>Paddle</strong> — payments. Acts as merchant of record for paid
          plans and receives your billing details directly; we never see or store your
          card details.</li>
        <li><strong>Sentry</strong> — error monitoring, so we can find and fix crashes.
          Configured not to send personal data.</li>
        <li><strong>UserJot</strong> — the in-app feedback and roadmap widget. Receives
          your user id, email address and name so feedback can be attributed. It loads
          only after you sign in, so it never runs for visitors to our public pages.</li>
        <li><strong>Our email provider</strong> — delivers invitations, notifications and
          reminders you have asked for.</li>
      </UL>
      <P>
        We will update this list before adding a new sub-processor. If you need a data
        processing agreement, email {SELLER.email} and we will provide one.
      </P>

      <H2>Cookies and tracking</H2>
      <P>
        Our public pages set no cookies beyond what is strictly necessary, and use no
        advertising or cross-site tracking. That is why you are not asked to accept
        cookies here — there is nothing to consent to.
      </P>
      <P>
        Once you sign in, we store a session so you stay signed in, and we record which
        features are used — for example that an event was created — so we can see where
        the product is confusing. Those records are tied to your account and are
        anonymised if you delete it. They are never shared or sold.
      </P>

      <H2>Security</H2>
      <P>
        Your data is held in the EU. Access is controlled at the database level,
        so one account cannot read another's data even if the application were
        wrong. Traffic is encrypted in transit, and we do not store payment card
        details at any point — those go directly to Paddle.
      </P>
      <P>
        No online service is perfectly secure, and we will not pretend otherwise.
        If a breach affects your personal data we will tell the relevant
        supervisory authority within 72 hours of becoming aware of it, and tell
        you directly where the law requires it.
      </P>

      <H2>Sensitive information</H2>
      <P>
        A calendar can end up holding sensitive things — medical appointments,
        religious observance, children's routines. We do not ask for that
        information and we do not analyse the content of your events. Please
        consider what you record and, particularly, what you put into a shared
        space or publish through a subscription link, since anyone with that link
        can read it.
      </P>

      <H2>Data Retention</H2>
      <P>
        Your account data is retained while your account is active. Deleting your
        account removes your personal data from our systems immediately; usage records
        are kept but anonymised, so they can no longer be linked to you.
      </P>
      <P>
        Billing records are held by Paddle, which retains them for as long as tax law
        requires. Email addresses collected during the closed beta are retained from
        that period and are no longer added to.
      </P>

      <H2>Your Rights</H2>
      <P>You have the right to:</P>
      <UL>
        <li>Access, update, or delete your personal information.</li>
        <li>Export your calendar data.</li>
        <li>Request deletion of your account and all associated data.</li>
      </UL>
      <P>
        To exercise any of these rights, contact us at the email address below.
      </P>

      <H2>Cookies</H2>
      <P>
        We use essential cookies for authentication (session tokens). We do not use
        advertising or third-party tracking cookies.
      </P>

      <H2>Changes to This Policy</H2>
      <P>
        We may update this policy from time to time. We will notify you of significant
        changes by posting a notice on the app or sending you an email.
      </P>

      <H2>Contact</H2>
      <P>
        If you have questions about this privacy policy, please contact us at{' '}
        <a
          href="mailto:hello@mozayc.io"
          style={{ color: 'var(--accent-primary)' }}
          className="underline"
        >
          hello@mozayc.io
        </a>.
      </P>
    </LegalPage>
  );
}

export function TermsOfService({ onBack }: { onBack: () => void }) {
  return (
    <LegalPage title="Terms of Service" onBack={onBack}>
      <P>
        These terms govern your use of Mozayc ("the Service"), operated at mozayc.io.
        By using the Service, you agree to these terms.
      </P>

      <H2>Who you are contracting with</H2>
      {SELLER_CONFIGURED ? (
        <P>
          Mozayc is operated by {SELLER.name}, trading as {SELLER.tradingAs},
          registered in {SELLER.country} ({SELLER.taxId}), of {SELLER.address}.
          References to "we" and "us" mean {SELLER.name}.
        </P>
      ) : (
        <P>
          Mozayc is operated by the {SELLER.tradingAs} team. Full registration
          details are published here before any paid plan is offered; while the
          Service is free, reach us at {SELLER.email}.
        </P>
      )}
      <P>
        Paid subscriptions are sold and invoiced by our payment provider, Paddle,
        acting as merchant of record. Your purchase contract for payment is with
        Paddle; your contract for the Service itself is with us under these terms.
      </P>

      <H2>The Service</H2>
      <P>
        Mozayc is a calendar and planning application. The Service is provided "as is".
        We may change or discontinue features, and will give reasonable notice of
        changes that materially reduce what a paid plan includes.
      </P>

      <H2>Accounts</H2>
      <P>
        You are responsible for maintaining the security of your account. You must
        provide a valid email address to create an account. One person may not maintain
        more than one account.
      </P>

      <H2>Your Content</H2>
      <P>
        You retain ownership of all content you create in Mozayc (events, tasks, tags, etc.).
        We do not claim any intellectual property rights over your content. You grant us
        a limited license to store and display your content solely for the purpose of
        providing the Service.
      </P>

      <H2>Shared Spaces</H2>
      <P>
        When you share a space with others, the content in that space becomes visible to
        all members according to their assigned roles. You are responsible for the content
        you share and for managing access to your spaces.
      </P>

      <H2>Acceptable Use</H2>
      <P>You agree not to:</P>
      <UL>
        <li>Use the Service for any unlawful purpose.</li>
        <li>Attempt to gain unauthorized access to other users' data.</li>
        <li>Interfere with or disrupt the Service's infrastructure.</li>
        <li>Use automated means to access the Service without permission.</li>
      </UL>

      <H2>Plans and fees</H2>
      <P>
        Mozayc has a free plan and paid plans. Prices are shown on our pricing page in
        euros and include any tax that applies to you, which is calculated at checkout
        based on your location. The free plan is free indefinitely and is not a trial.
      </P>
      <P>
        Paid plans are billed in advance, monthly or annually according to the plan you
        choose, and <strong>renew automatically</strong> at the end of each period until
        cancelled. We will tell you by email before any price change takes effect, and
        you may cancel before it does.
      </P>

      <H2>Cancelling</H2>
      <P>
        You can cancel a paid plan at any time from your account settings. Cancellation
        takes effect at the end of the period you have already paid for — you keep
        access until then, and are not charged again.
      </P>
      <P>
        Cancelling never deletes your data. Your account reverts to the free plan and
        everything you created remains yours, subject to the free plan's limits.
      </P>

      <H2>Refunds</H2>
      <P>
        <strong>If you are not happy with a paid plan, tell us within 14 days of the
        charge and we will refund it in full.</strong> No reason is required. This
        applies to each new subscription and to each renewal.
      </P>
      <P>
        This is in addition to any statutory right of withdrawal you may have as a
        consumer. Refunds are processed by Paddle to the original payment method.
        Contact {SELLER.email} or use the receipt Paddle sent you.
      </P>

      <H2>Availability and data</H2>
      <P>
        We aim to keep the Service available and your data safe, but we do not offer a
        guaranteed uptime level. We keep backups; you should keep your own copies of
        anything critical. You can export everything you have created at any time from
        Settings, in a machine-readable format.
      </P>

      <H2>What we do and do not promise</H2>
      <P>
        We will provide the Service with reasonable care and skill. We do not
        promise that it will be uninterrupted, error-free, or that it will meet
        any particular requirement of yours. Features may change.
      </P>
      <P>
        Mozayc is a planning tool, not a system of record. It is not designed or
        certified for use where an error could cause harm — for example clinical,
        legal, financial or safety-critical scheduling — and you should not rely
        on it as the only record of anything important.
      </P>
      <P>
        <strong>Keep your own copies.</strong> We take backups and take security
        seriously, but no online service is immune to loss. You can export
        everything you have created at any time from Settings, and you are
        responsible for keeping copies of anything you cannot afford to lose.
      </P>

      <H2>Things outside our control</H2>
      <P>
        The Service depends on others: Supabase for data, Vercel for hosting,
        Paddle for payments, and — where you connect them — Google Calendar and
        any iCal feed you subscribe to. We are not responsible for those
        services failing, changing, or ending, nor for the accuracy of calendar
        data we import on your instruction.
      </P>
      <P>
        Nor are we liable for failures caused by events beyond our reasonable
        control, including outages at those providers, network failures, or acts
        of government.
      </P>

      <H2>Your responsibilities</H2>
      <P>
        You are responsible for keeping your account secure, for the content you
        create and share, and for having the right to share it. If you invite
        people into a space, you are responsible for who you invite and what you
        expose to them.
      </P>

      <H2>Limitation of liability</H2>
      <P>
        <strong>Nothing in these terms limits or excludes our liability for death
        or personal injury caused by our negligence, for fraud or fraudulent
        misrepresentation, or for anything else that cannot lawfully be limited.</strong>
      </P>
      <P>
        <strong>If you are a consumer:</strong> we are responsible for loss you
        suffer that is a foreseeable result of our breaking these terms or
        failing to use reasonable care and skill. We are not responsible for loss
        that is not foreseeable. Nothing here affects your statutory rights,
        which apply in addition to these terms and which we do not attempt to
        reduce.
      </P>
      <P>
        <strong>If you use the Service for business purposes:</strong> we are not
        liable for loss of profit, loss of business, loss of anticipated savings,
        business interruption, or loss or corruption of data, however caused; and
        our total liability arising out of or in connection with these terms is
        limited to the amount you paid us in the twelve months before the claim
        arose, or one hundred euros if you paid us nothing.
      </P>
      <P>
        Business claims must be brought within twelve months of the date you
        became aware, or ought reasonably to have become aware, of the
        circumstances giving rise to them. This does not apply to consumers,
        whose statutory time limits are unaffected.
      </P>

      <H2>Indemnity (business customers only)</H2>
      <P>
        If you use the Service for business purposes, you agree to indemnify us
        against claims, damages and reasonable costs arising from your content,
        your use of the Service in breach of these terms, or your infringement of
        someone else's rights. This clause does not apply to consumers.
      </P>

      <H2>Termination</H2>
      <P>
        You may delete your account at any time from Settings. We may suspend or
        terminate access where these terms are breached, where required by law, or where
        an account is used to harm the Service or other users. If we terminate a paid
        account other than for such a reason, we will refund the unused part of the
        period you have paid for.
      </P>

      <H2>Governing law</H2>
      <P>
        {SELLER_CONFIGURED
          ? `These terms are governed by the law of ${SELLER.country}, and any dispute will be subject to its courts.`
          : 'These terms are governed by the law of the country in which the Service is operated.'}{' '}
        If you are a consumer, this does not deprive you of the protection of
        mandatory consumer law in your own country of residence, nor of the right
        to bring proceedings there.
      </P>

      <H2>General</H2>
      <P>
        If any part of these terms is found to be unenforceable, the rest
        continues to apply. If we do not enforce a right straight away, we do not
        lose it. We may transfer these terms to someone else if the Service
        changes hands; your rights are unaffected and we will tell you first. You
        may not transfer your account to someone else without our agreement.
      </P>
      <P>
        These terms, together with our Privacy Policy, are the whole agreement
        between us about the Service.
      </P>

      <H2>Suspension for non-payment</H2>
      <P>
        If a payment fails, Paddle will retry it and let you know. If it remains
        unpaid, the account reverts to the free plan — we do not delete anything,
        and you can settle and resume at any time.
      </P>

      <H2>Changes to These Terms</H2>
      <P>
        We may change these terms. For minor changes, the updated version takes
        effect when posted. For changes that materially affect your rights or what
        a paid plan includes, we will tell paid customers by email at least 30 days
        beforehand, and you may cancel before they take effect. Continued use after
        that constitutes acceptance.
      </P>

      <H2>Contact</H2>
      <P>
        Questions about these terms? Contact us at{' '}
        <a
          href="mailto:hello@mozayc.io"
          style={{ color: 'var(--accent-primary)' }}
          className="underline"
        >
          hello@mozayc.io
        </a>.
      </P>
    </LegalPage>
  );
}
